import { gql, useMutation } from '@apollo/client'
import CancelIcon from '@mui/icons-material/Cancel'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import ZoomOutIcon from '@mui/icons-material/ZoomOut'
import {
  Box,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  Slider,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material'
import Modal from 'components/Modal'
import { Gantt, Task, ViewMode } from 'gantt-task-react'
import 'gantt-task-react/dist/index.css'
import { addMonths, addDays } from 'date-fns'
import React, { useContext, useEffect, useState } from 'react'
import { Task as ITask } from 'utils/__generated__/graphql'
import getDurationObj from '../../utils/getDurationObj'

import { TaskDataType, TaskForm } from '../Create'
import { TASK_FRAGMENT } from '../constants'
import { TIMELINE_FRAGMENT } from '../../constants'
import TimelineContext, { TimelineContextType } from '../context'
import TaskListHeader from './TaskListHeader'
import TaskListTable from './TaskListTable'
import TooltipContent from './TooltipContent'

const UPDATE_TASK = gql`
  ${TIMELINE_FRAGMENT}
  ${TASK_FRAGMENT}
  mutation UPDATE_TASK($timelineId: ID!, $task: TaskInput!) {
    updateTask(timelineId: $timelineId, task: $task) {
      ...TimelineFragment
      tasks {
        ...TaskFragment
      }
    }
  }
`

const UPDATE_TASKS = gql`
  ${TIMELINE_FRAGMENT}
  ${TASK_FRAGMENT}
  mutation UPDATE_TASKS($timelineId: ID!, $tasks: [TaskInput!]!) {
    updateTasks(timelineId: $timelineId, tasks: $tasks) {
      ...TimelineFragment
      tasks {
        ...TaskFragment
      }
    }
  }
`

type GanttTask = Task & {
  mode?: string | null
  startDependencies?: (string | null)[] | null
  endDependencies?: (string | null)[] | null
  notes?: string | null
  assignee?: string | null
  displayOrder?: number | null
  duration?: { months?: number | null; days?: number | null } | null
  offset?: { months?: number | null; days?: number | null } | null
}

function GanttChart() {
  const { timeline } = useContext<TimelineContextType>(TimelineContext)
  const [view, setView] = useState<ViewMode>(ViewMode.Year)
  const [tasks, setTasks] = useState<GanttTask[]>([])
  const [open, setOpen] = useState<boolean>(false)
  const [selectedTask, setSelectedTask] = useState<ITask>({} as ITask)
  const [checked, setChecked] = useState<boolean>(true)
  const [columnWidth, setColumnWidth] = useState<number>(120)

  const [updateTask] = useMutation(UPDATE_TASK)
  const [updateTasks] = useMutation(UPDATE_TASKS)
  const theme = useTheme()

  const handleOpen = () => setOpen(true)
  const handleClose = () => {
    setSelectedTask({})
    setOpen(false)
  }

  useEffect(() => {
    if (timeline.tasks) {
      const formattedTasks: GanttTask[] = (timeline.tasks || [])
        .filter(Boolean)
        .map(task => {
          const now = new Date()
          const start = addMonths(
            addDays(now, task!.offset?.days || 0),
            task!.offset?.months || 0
          )
          const end = addMonths(
            addDays(start, task!.duration?.days || 0),
            task!.duration?.months || 0
          )

          return {
            id: task!.id || '',
            name: task!.name || '',
            type: (task!.type as Task['type']) || 'task',
            start: task!.start ? new Date(task!.start) : start,
            end: task!.end ? new Date(task!.end) : end,
            progress: task!.progress || 0,
            dependencies: (task!.startDependencies || []).filter(
              Boolean
            ) as string[],
            styles: { backgroundColor: task!.styles?.backgroundColor || '' },
            project: task!.project || undefined,
            displayOrder: task!.displayOrder || undefined,
            notes: task!.notes || undefined,
            assignee: task!.assignee || undefined,
            mode: task!.mode || undefined,
            startDependencies: task!.startDependencies,
            endDependencies: task!.endDependencies,
            duration: task!.duration,
            offset: task!.offset,
          }
        })

      setTasks(formattedTasks)
    }
  }, [timeline.tasks])

  const handleTimeline = (data: TaskDataType) => {
    const { start, end, mode, ...restTask } = data
    updateTask({
      variables: {
        timelineId: timeline.id,
        task: {
          id: selectedTask.id,
          ...restTask,
          duration: getDurationObj(start, end),
          offset: getDurationObj(new Date(), start),
          mode,
          ...(mode === 'STATIC' ? { start, end } : { start: null, end: null }),
        },
      },
      onCompleted: handleClose,
    })
  }

  const handleTaskChange = (task: Task) => {
    const newTasks = tasks.map(t => (t.id === task.id ? { ...t, ...task } : t))
    setTasks(newTasks)

    const formattedNewTasks = newTasks.map(task => {
      const {
        id,
        name,
        type,
        project,
        startDependencies,
        endDependencies,
        progress,
        displayOrder,
        notes,
        assignee,
        start,
        end,
        mode,
        styles,
      } = task

      return {
        id,
        name,
        type,
        project,
        startDependencies,
        endDependencies,
        progress,
        displayOrder,
        notes,
        assignee,
        styles: { backgroundColor: styles?.backgroundColor },
        duration: getDurationObj(start, end),
        offset: getDurationObj(new Date(), start),
        ...(mode === 'STATIC' ? { start, end } : { start: null, end: null }),
        mode,
      }
    })

    updateTasks({
      variables: { timelineId: timeline.id, tasks: formattedNewTasks },
      optimisticResponse: {
        __typename: 'Mutation',
        updateTasks: {
          ...timeline,
          tasks: formattedNewTasks.map(task => ({
            __typename: 'Task',
            ...task,
            duration: { ...task.duration, __typename: 'TimeInterval' },
            offset: {
              __typename: 'TimeInterval',
              months: task.offset?.months ?? 0,
              days: task.offset?.days ?? 0,
            },
            styles: { ...task.styles, __typename: 'TaskStyles' },
          })),
        },
      },
    })
  }

  const handleDblClick = (task: Task) => {
    setSelectedTask({
      ...task,
      styles: { backgroundColor: task.styles?.backgroundColor },
    })
    handleOpen()
  }

  const handleToggleChange = (
    _: React.MouseEvent<HTMLElement>,
    toggleVal: ViewMode
  ) => {
    setView(toggleVal)
  }

  const handleWidthChange = (_: Event, newValue: number | number[]) => {
    if (typeof newValue === 'number') setColumnWidth(newValue)
  }

  const {
    id,
    start,
    end,
    name,
    assignee,
    startDependencies,
    endDependencies,
    notes,
    styles,
    type,
    duration,
    mode,
  } = selectedTask

  return (
    <Stack spacing={1}>
      <Modal open={open} width="75%" maxWidth={720}>
        <Stack
          direction="row"
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Typography
            sx={{ ...theme.typography.h5, color: theme.palette.text.primary }}
          >
            Edit - {name}
          </Typography>
          <CancelIcon
            onClick={handleClose}
            sx={{ cursor: 'pointer', color: theme.palette.error.main }}
          />
        </Stack>
        <Divider sx={{ mt: 1, mb: 1 }} />
        <TaskForm
          onDeleteSuccess={handleClose}
          values={{
            id: id || '',
            type: type || '',
            start,
            end,
            mode: mode || '',
            name: name || '',
            assignee: assignee || '',
            startDependencies: (startDependencies || []).filter(
              Boolean
            ) as string[],
            endDependencies: (endDependencies || []).filter(
              Boolean
            ) as string[],
            notes: notes || '',
            styles: { backgroundColor: styles?.backgroundColor || '' },
            duration: {
              months: duration?.months || 0,
              days: duration?.days || 0,
            },
          }}
          onTask={handleTimeline}
        />
      </Modal>
      <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={checked}
                onChange={e => setChecked(e.target.checked)}
              />
            }
            label={<Typography variant="caption">Show List</Typography>}
          />
        </FormGroup>
        <Stack direction="row" spacing={1}>
          <Box sx={{ width: 200 }}>
            <Stack spacing={1} direction="row" sx={{ alignItems: 'center' }}>
              <IconButton
                onClick={() => setColumnWidth(columnWidth - 10)}
                disabled={columnWidth === 40}
              >
                <ZoomOutIcon />
              </IconButton>
              <Slider
                value={columnWidth}
                onChange={handleWidthChange}
                min={40}
                max={240}
              />
              <IconButton
                onClick={() => setColumnWidth(columnWidth + 10)}
                disabled={columnWidth === 240}
              >
                <ZoomInIcon />
              </IconButton>
            </Stack>
          </Box>
          <ToggleButtonGroup
            color="primary"
            value={view}
            exclusive
            size="small"
            onChange={handleToggleChange}
          >
            <ToggleButton value={ViewMode.Month}>Month</ToggleButton>
            <ToggleButton value={ViewMode.Year}>Year</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Stack>
      {tasks.length > 0 && (
        <Gantt
          tasks={tasks}
          viewMode={view}
          onDateChange={handleTaskChange}
          onDoubleClick={handleDblClick}
          columnWidth={columnWidth}
          listCellWidth={checked ? '124px' : ''}
          fontFamily="inherit"
          TooltipContent={TooltipContent}
          TaskListTable={props => (
            <TaskListTable {...props} setSelectedTask={handleDblClick} />
          )}
          TaskListHeader={TaskListHeader}
        />
      )}
    </Stack>
  )
}

export default GanttChart
